import { LinkButton } from "@/ui/next/LinkButton";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import localizeHref from "../../../../lib/multilingual/localizeHref";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { NEW_IN_ROUTE } from "@/common/static/routes.static";
import { ProductCard } from "@/features/search/components/product-card/ProductCard";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";
import { toAnalyticsProduct } from "@/features/search/utils/analytics.utils";
import {
  NEW_THIS_WEEK_MIN_ITEMS,
  newThisWeekCopy,
  newThisWeekHomepageCopy,
} from "@/features/search/utils/new-this-week.utils";
import { Heading } from "@/ui/cva/Heading";

/**
 * Cards the homepage shows below `md` (three rows of two). The rest stay in the HTML, so every
 * product link remains crawlable, but are hidden until the grid widens.
 */
const HOMEPAGE_MOBILE_ITEMS = 6;

/** The list id the clicks report under, so the module can be told apart from the grid in GA. */
const LIST_ID = "new_this_week";

/**
 * Where the module sits. `listing` is the landing-page block below the grid; `homepage` is the
 * homepage section, which uses homepage copy, links to the full "new" listing page, and shows
 * two cards per row on mobile like every other homepage section.
 */
type NewThisWeekVariant = "listing" | "homepage";

const LAYOUT: Record<NewThisWeekVariant, { section: string; list: string }> = {
  listing: {
    section: "flex flex-col space-y-6 border-t py-8 md:py-10",
    list: "grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4 xl:grid-cols-6",
  },
  homepage: {
    section: "flex flex-col space-y-6",
    list: "grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4 lg:grid-cols-6",
  },
};

/**
 * The newest listings, server-rendered so every fresh product is linked from an indexable page
 * on the day it goes live (see `utils/new-this-week.utils.ts` for the Search Console evidence).
 * On landing pages it sits below the grid (`SearchLayout`); on the homepage below the marketing
 * block (`Homepage`).
 *
 * The cards are the grid's own `ProductCard`, so images (lazy), prices, badges and click
 * tracking are identical, and each is a plain crawlable `<a>` to the localized product URL.
 */
export const NewThisWeek = ({
  products,
  variant = "listing",
}: {
  products: ProductCardProduct[];
  variant?: NewThisWeekVariant;
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  if (products.length < NEW_THIS_WEEK_MIN_ITEMS) {
    return null;
  }

  const isHomepage = variant === "homepage";
  const copy = isHomepage
    ? newThisWeekHomepageCopy(locale)
    : newThisWeekCopy(locale);
  const layout = LAYOUT[variant];

  return (
    <section className={layout.section} data-testid="new-this-week">
      <div className="flex flex-col space-y-1">
        <Heading as="h2" size="h3">
          {t("listing:listing-new-this-week-title", copy.title)}
        </Heading>
        <p className="font-body text-sm text-grey-700">
          {isHomepage
            ? t("homepage:homepage-new-this-week-subtitle", copy.subtitle)
            : t("listing:listing-new-this-week-subtitle", copy.subtitle)}
        </p>
        {isHomepage && (
          /* Link localizes a path only when told the source locale (see HomepageHeroBlock). */
          <LinkButton
            href={localizeHref(NEW_IN_ROUTE, "en", locale)}
            appearance="minimal"
            className="-ml-6 self-start"
            onClick={() => analyticsService.trackHpNewIn("link")}
          >
            {t(
              "homepage:homepage-new-this-week-view-all",
              newThisWeekHomepageCopy(locale).viewAll
            )}
          </LinkButton>
        )}
      </div>

      <ul className={layout.list}>
        {products.map((product, index) => (
          <li
            key={product.id}
            className={
              isHomepage && index >= HOMEPAGE_MOBILE_ITEMS
                ? "hidden md:block"
                : undefined
            }
          >
            <ProductCard
              product={product}
              onSelect={() => {
                analyticsService.trackSelectItem(
                  index + 1,
                  LIST_ID,
                  LIST_ID,
                  toAnalyticsProduct(product),
                  { fromSearch: true }
                );

                if (isHomepage) {
                  analyticsService.trackHpNewIn("tile");
                }
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};
